export * from './types.ts';
export * from './queries.ts';

/**
 * Per-user chapter read state for the BookReader.
 *
 * Backed by `books.user_progress` on the server; both hooks are thin
 * TanStack Query wrappers. Keyed by OL work key (e.g. `/works/OL15992072W`)
 * since that's what the mobile router has on hand — the server resolves it
 * to the internal uuid. Chapter indices are 1-based to match URLs.
 */

import { useCallback } from 'react';
import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';
import { useGql } from '@ah/design';
import {
  BOOK_PROGRESS_QUERY,
  BOOK_PROGRESS_MANY_QUERY,
  RECORD_BOOK_CHAPTER_READ_MUTATION,
} from './queries.ts';

function olKeyFromOlid(olid: string): string {
  return `/works/${olid}`;
}

interface BookProgress {
  chaptersRead: number[];
}

const EMPTY: BookProgress = { chaptersRead: [] };

/** Live-updating progress for a single book. `olid` is the URL-style id (e.g. `OL15992072W`). */
export function useBookProgress(olid: string): BookProgress {
  const gql = useGql();
  const olKey = olKeyFromOlid(olid);
  const { data } = useQuery({
    queryKey: ['books', 'progress', olKey],
    queryFn: async () => {
      const res = await gql.request<{ bookProgress: { olKey: string; chaptersRead: number[] } }>(
        BOOK_PROGRESS_QUERY,
        { olKey },
      );
      return res.bookProgress;
    },
    enabled: !!olid,
  });
  return data ? { chaptersRead: data.chaptersRead } : EMPTY;
}

/**
 * Bulk progress for a list of books. Returns a map of olid -> { read, total }.
 * `totals` is the {olid: chapterCount} map — pass what you got from the list query.
 */
export function useManyBookProgress(totals: Record<string, number>): Record<string, { read: number; total: number }> {
  const gql = useGql();
  const olids = Object.keys(totals);
  const olKeys = olids.map(olKeyFromOlid);
  const cacheKey = olids.join(',');
  const { data } = useQuery({
    queryKey: ['books', 'progress-many', cacheKey],
    queryFn: async () => {
      const res = await gql.request<{ bookProgressMany: Array<{ olKey: string; chaptersRead: number[] }> }>(
        BOOK_PROGRESS_MANY_QUERY,
        { olKeys },
      );
      return res.bookProgressMany;
    },
    enabled: olids.length > 0,
  });
  const out: Record<string, { read: number; total: number }> = {};
  const byOlid = new Map<string, number[]>();
  for (const r of data ?? []) {
    const olid = r.olKey.replace(/^\/works\//, '');
    byOlid.set(olid, r.chaptersRead);
  }
  for (const olid of olids) {
    out[olid] = { read: byOlid.get(olid)?.length ?? 0, total: totals[olid] ?? 0 };
  }
  return out;
}

/** Imperative hook to mark a chapter as read. Returns a stable callback. */
export function useMarkChapterRead(): (olid: string, chapter: number) => void {
  const gql = useGql();
  const qc = useQueryClient();
  const mutation = useMutation({
    mutationFn: async (vars: { olid: string; chapter: number }) => {
      const olKey = olKeyFromOlid(vars.olid);
      const res = await gql.request<{
        recordBookChapterRead: { olKey: string; chaptersRead: number[] };
      }>(RECORD_BOOK_CHAPTER_READ_MUTATION, { olKey, chapter: vars.chapter });
      return res.recordBookChapterRead;
    },
    onSuccess: (data, vars) => {
      const olKey = olKeyFromOlid(vars.olid);
      qc.setQueryData(['books', 'progress', olKey], data);
      void qc.invalidateQueries({ queryKey: ['books', 'progress-many'] });
    },
  });
  return useCallback(
    (olid: string, chapter: number) => {
      mutation.mutate({ olid, chapter });
    },
    [mutation],
  );
}
